import axios, { AxiosInstance } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (window.location.port === '3000' ? 'http://localhost:8007' : '/api');

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const refreshToken = localStorage.getItem('refresh_token');
  if (refreshToken) config.headers['X-Refresh-Token'] = refreshToken;
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, { refresh_token: refreshToken });
          localStorage.setItem('access_token', response.data.access_token);
          localStorage.setItem('refresh_token', response.data.refresh_token);
          originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`;
          return apiClient(originalRequest);
        } catch {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;

export interface User {
  id: string;
  username?: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

export interface Evaluation {
  id: string;
  name: string;
  product_type?: string;
  description?: string;
  status: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface Criterion {
  id: string;
  name: string;
  description?: string;
  sort_order: number;
}

export interface Target {
  id: string;
  vendor?: string;
  product_name: string;
  description?: string;
  notes?: string;
  sort_order: number;
}

export interface EvaluationResult {
  id: string;
  evaluation_id: string;
  engine_provider: string;
  engine_model: string;
  status: string;
  matrix: any;
  created_at: string;
}

export interface EngineModelOption {
  id: string;
  label: string;
}

export interface EngineModelProvider {
  provider: string;
  default_model: string;
  models: EngineModelOption[];
  error?: string;
}

export const authApi = {
  register: (payload: any) => apiClient.post('/auth/register', payload),
  login: (payload: any) => apiClient.post('/auth/login', payload),
  profile: () => apiClient.get<User>('/auth/profile'),
  logout: (refreshToken: string) => apiClient.post('/auth/logout', { refresh_token: refreshToken }),
};

export const settingsApi = {
  get: () => apiClient.get('/settings'),
  update: (payload: { theme: string }) => apiClient.patch('/settings', payload),
};

export const evaluationApi = {
  engineModels: () => apiClient.get<EngineModelProvider[]>('/evaluations/engine-models'),
  list: () => apiClient.get<Evaluation[]>('/evaluations'),
  create: (payload: Partial<Evaluation>) => apiClient.post<Evaluation>('/evaluations', payload),
  update: (id: string, payload: Partial<Evaluation>) => apiClient.patch<Evaluation>(`/evaluations/${id}`, payload),
  reorder: (evaluationIds: string[]) => apiClient.post<Evaluation[]>('/evaluations/reorder', { evaluation_ids: evaluationIds }),
  remove: (id: string) => apiClient.delete(`/evaluations/${id}`),
  criteria: (id: string) => apiClient.get<Criterion[]>(`/evaluations/${id}/criteria`),
  createCriterion: (id: string, payload: Partial<Criterion>) => apiClient.post<Criterion>(`/evaluations/${id}/criteria`, payload),
  updateCriterion: (id: string, criterionId: string, payload: Partial<Criterion>) => apiClient.patch<Criterion>(`/evaluations/${id}/criteria/${criterionId}`, payload),
  deleteCriterion: (id: string, criterionId: string) => apiClient.delete(`/evaluations/${id}/criteria/${criterionId}`),
  importCriteria: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<Criterion[]>(`/evaluations/${id}/criteria/import`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  copyCriteria: (id: string, sourceId: string) => apiClient.post<Criterion[]>(`/evaluations/${id}/criteria/copy-from/${sourceId}`),
  targets: (id: string) => apiClient.get<Target[]>(`/evaluations/${id}/targets`),
  createTarget: (id: string, payload: Partial<Target>) => apiClient.post<Target>(`/evaluations/${id}/targets`, payload),
  updateTarget: (id: string, targetId: string, payload: Partial<Target>) => apiClient.patch<Target>(`/evaluations/${id}/targets/${targetId}`, payload),
  deleteTarget: (id: string, targetId: string) => apiClient.delete(`/evaluations/${id}/targets/${targetId}`),
  importTargets: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<Target[]>(`/evaluations/${id}/targets/import`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  copyTargets: (id: string, sourceId: string) => apiClient.post<Target[]>(`/evaluations/${id}/targets/copy-from/${sourceId}`),
  run: (id: string, engines: { provider: string; model: string }[], aggregate = false) => apiClient.post<EvaluationResult[]>(`/evaluations/${id}/run`, { engines, aggregate }),
  results: (id: string) => apiClient.get<EvaluationResult[]>(`/evaluations/${id}/results`),
  deleteResult: (id: string, resultId: string) => apiClient.delete(`/evaluations/${id}/results/${resultId}`),
  exportResult: async (id: string, resultId: string, fallbackName = 'vendor_review_matrix.xlsx') => {
    const response = await apiClient.get(`/evaluations/${id}/results/${resultId}/export`, { responseType: 'blob' });
    const disposition = response.headers['content-disposition'] || '';
    const filenameMatch = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
    const filename = filenameMatch ? decodeURIComponent(filenameMatch[1].replace(/"/g, '')) : fallbackName;
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};
